import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Provider, Client, Visit, Transportation, Location, OhioCounty, ClientISPOutcome, ClientISPExperience, ClientISPSupport, ClientISP } from '../types';

// Helper function to format date as MM-DD-YYYY
const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return '';
  try {
    const datePart = dateString.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      return '';
    }
    return `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}-${year}`;
  } catch (error) {
    return '';
  }
};

// Helper function to format time as HH:MM AM/PM
const formatTime = (dateString: string | undefined): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return '';
    }
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const minutesStr = String(minutes).padStart(2, '0');
    return `${hours}:${minutesStr} ${ampm}`;
  } catch (error) {
    return '';
  }
};

interface ServicesReportData {
  provider: Provider;
  client: Client;
  visits: Visit[];
  outcomes: ClientISPOutcome[];
  experiences: ClientISPExperience[];
  supports?: ClientISPSupport[];
  services: any[];
  serviceUnitConversions: any[];
  locations: Location[];
  ohioCounties: OhioCounty[];
  selectedISP?: ClientISP;
  startDate: string;
  endDate: string;
}

interface TransportationReportData {
  provider: Provider;
  client: Client;
  transportations: Transportation[];
  visits: Visit[];
  locations: Location[];
  ohioCounties: OhioCounty[];
  selectedISP?: any;
  startDate: string;
  endDate: string;
}

const getCountyName = (countyId: number | undefined, counties: OhioCounty[]): string => {
  if (!countyId) return '';
  const county = counties.find(c => c.county_id === countyId);
  return county ? county.county_name : '';
};

const getOutcomeTitle = (outcomeId: number | undefined, outcomes: ClientISPOutcome[]): string => {
  if (!outcomeId) return '';
  const outcome = outcomes.find(o => o.outcome_id === outcomeId);
  return outcome ? (outcome.outcome_title || '') : '';
};

const getExperienceTitle = (experienceId: number | undefined, experiences: ClientISPExperience[]): string => {
  if (!experienceId) return '';
  const experience = experiences.find(e => e.experience_id === experienceId);
  return experience ? (experience.experience_title || '') : '';
};

const getSupportTitle = (supportId: number | undefined, supports: ClientISPSupport[] = []): string => {
  if (!supportId) return '';
  const support = supports.find(s => s.support_id === supportId);
  return support ? (support.support_title || '') : '';
};

const formatAddress = (locationId: number | undefined, locations: Location[]): string => {
  if (!locationId) return '';
  const location = locations.find(l => l.location_id === locationId);
  if (!location) return '';
  
  const parts: string[] = [];
  if (location.location_address_line_1) parts.push(location.location_address_line_1);
  if (location.location_address_line_2) parts.push(location.location_address_line_2);
  if (location.location_city) parts.push(location.location_city);
  if (location.location_state) parts.push(location.location_state);
  if (location.location_zip) parts.push(location.location_zip);
  
  return parts.join(', ').toUpperCase();
};

const formatClientAddress = (client: Client): string => {
  const parts: string[] = [];
  if (client.address_line1) parts.push(client.address_line1);
  if (client.address_line2) parts.push(client.address_line2);
  const cityPart = [client.city, client.state].filter(Boolean).join(', ');
  const locality = [cityPart, client.zip_code].filter(Boolean).join(' ');
  if (locality) {
    parts.push(locality);
  }
  return parts.join(', ');
};

const getPlaceOfService = (visit: Visit, client: Client): string => {
  const address = formatClientAddress(client);
  const visitType = (visit.visit_type || '').toLowerCase();

  if (visitType === 'community') {
    return address ? `Community — ${address}` : 'Community';
  }
  if (visitType === 'home') {
    return address || 'Home';
  }
  return address || visit.visit_type || '';
};

const getUnitsFromMinutes = (minutes: number, serviceUnitConversions: any[]): number => {
  const conversion = serviceUnitConversions.find(
    (conv) => minutes >= conv.minimum_minutes && minutes <= conv.maximum_minutes
  );
  return conversion ? conversion.units : 0;
};

const calculateUnits = (visit: Visit, services: any[], serviceUnitConversions: any[]): string => {
  if (visit.service_units !== undefined && visit.service_units !== null) {
    return visit.service_units.toString();
  }
  
  const service = services.find(s => s.service_id === visit.service_id);
  if (!service) {
    return '0';
  }
  
  let minutesDiff = 0;
  if (visit.scheduled_start && visit.scheduled_end) {
    const start = new Date(visit.scheduled_start);
    const end = new Date(visit.scheduled_end);
    minutesDiff = Math.floor((end.getTime() - start.getTime()) / (1000 * 60));
  } else if (visit.duration) {
    minutesDiff = Math.floor(visit.duration / 60);
  } else {
    return '0';
  }
  
  if (service.service_unit === '15-minute') {
    const units = getUnitsFromMinutes(minutesDiff, serviceUnitConversions);
    return units.toString();
  } else if (['Day', 'Per Day', 'Over Night', 'Part Day'].includes(service.service_unit || '')) {
    if (visit.scheduled_start && visit.scheduled_end) {
      const start = new Date(visit.scheduled_start);
      const end = new Date(visit.scheduled_end);
      const daysDiff = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
      return daysDiff.toString();
    }
    return '0';
  } else if (service.service_unit === 'Hour') {
    if (visit.scheduled_start && visit.scheduled_end) {
      const start = new Date(visit.scheduled_start);
      const end = new Date(visit.scheduled_end);
      const hoursDiff = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60));
      return hoursDiff.toString();
    }
    return '0';
  }
  
  return '0';
};

const getVisitRatio = (visitId: number | undefined, visits: Visit[]): string => {
  if (!visitId) return '';
  const visit = visits.find(v => v.visit_id === visitId);
  return visit?.ratio || '';
};

const calculateMiles = (transportation: Transportation): string => {
  if (transportation.ending_miles !== undefined && transportation.starting_miles !== undefined) {
    const miles = transportation.ending_miles - transportation.starting_miles;
    return miles >= 0 ? miles.toString() : '';
  }
  if (transportation.miles !== undefined) {
    return transportation.miles.toString();
  }
  return '';
};

export const generateServicesReportPDF = (data: ServicesReportData): void => {
  const doc = new jsPDF('landscape', 'pt', 'a4');
  
  // Title
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('HPC SERVICE DELIVERY AND OUTCOME REPORT', doc.internal.pageSize.getWidth() / 2, 40, { align: 'center' });
  
  let yPos = 80;
  
  // Header Information
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  
  const leftCol = 50;
  const rightCol = 350;
  const lineHeight = 15;
  
  doc.setFont('helvetica', 'bold');
  doc.text('Individual\'s Name:', leftCol, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(`${data.client.first_name} ${data.client.last_name}`, leftCol + 100, yPos);
  
  doc.setFont('helvetica', 'bold');
  doc.text('Individual\'s Medicaid Number:', rightCol, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(data.client.medicaid_num ? String(data.client.medicaid_num) : 'N/A', rightCol + 150, yPos);
  
  yPos += lineHeight;
  
  const ispSpan = data.selectedISP && data.selectedISP.span_start_date && data.selectedISP.span_end_date
    ? `${formatDate(data.selectedISP.span_start_date)} - ${formatDate(data.selectedISP.span_end_date)}`
    : '_____________________';
  
  doc.setFont('helvetica', 'bold');
  doc.text('ISP Span:', leftCol, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(ispSpan, leftCol + 60, yPos);
  
  const dateRange = data.startDate && data.endDate
    ? `${formatDate(data.startDate)} - ${formatDate(data.endDate)}`
    : '';
  
  doc.setFont('helvetica', 'bold');
  doc.text('Date Range:', rightCol, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(dateRange, rightCol + 80, yPos);
  
  yPos += lineHeight;
  
  doc.setFont('helvetica', 'bold');
  doc.text('Provider\'s Name:', leftCol, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(`${data.provider.first_name} ${data.provider.last_name}`, leftCol + 100, yPos);
  
  doc.setFont('helvetica', 'bold');
  doc.text('Provider\'s Contract Number:', rightCol, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(data.provider.contract_number || 'N/A', rightCol + 150, yPos);
  
  yPos += lineHeight;
  
  doc.setFont('helvetica', 'bold');
  doc.text('County:', leftCol, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(getCountyName(data.client.county_id, data.ohioCounties), leftCol + 50, yPos);
  
  doc.setFont('helvetica', 'bold');
  doc.text('Provider\'s Electronic Signature:', rightCol, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text('_____________________', rightCol + 150, yPos);
  
  yPos += 30;
  
  // Prepare table data
  const tableData = data.visits.map(visit => [
    formatDate(visit.visit_date || visit.scheduled_start),
    formatTime(visit.scheduled_start),
    formatTime(visit.scheduled_end),
    calculateUnits(visit, data.services, data.serviceUnitConversions),
    visit.ratio || '',
    getPlaceOfService(visit, data.client),
    getOutcomeTitle(visit.outcome_id, data.outcomes),
    getExperienceTitle(visit.experience_id, data.experiences),
    getSupportTitle(visit.support_id, data.supports || []),
    visit.unusual_incident_flag ? 'Yes' : 'No',
    visit.notes || ''
  ]);
  
  // Generate table
  // A4 landscape: 842pt width, with 40pt margins = 762pt available
  // Proportional widths: Date(0.8) + TimeIn(0.8) + TimeOut(0.8) + Units(0.6) + Ratio(0.6) + PlaceOfService(1.0) + Outcome(1.2) + Experience(1.2) + Support(1.0) + UI(0.5) + Notes(1.5) = 10.0
  const availableWidth = 762;
  const totalProportion = 10.0;
  const baseWidth = availableWidth / totalProportion;
  
  autoTable(doc, {
    startY: yPos,
    head: [['Date', 'Time In', 'Time Out', 'Units', 'Ratio', 'Place of Service', 'Outcome(s)', 'Experience(s)', 'Support(s)', 'UI?', 'Notes']],
    body: tableData,
    theme: 'grid',
    headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7 },
    bodyStyles: { fontSize: 6 },
    columnStyles: {
      0: { cellWidth: baseWidth * 0.8 }, // Date
      1: { cellWidth: baseWidth * 0.8 }, // Time In
      2: { cellWidth: baseWidth * 0.8 }, // Time Out
      3: { cellWidth: baseWidth * 0.6 }, // Units
      4: { cellWidth: baseWidth * 0.6 }, // Ratio
      5: { cellWidth: baseWidth * 1.0 }, // Place of Service
      6: { cellWidth: baseWidth * 1.2 }, // Outcome(s)
      7: { cellWidth: baseWidth * 1.2 }, // Experience(s)
      8: { cellWidth: baseWidth * 1.0 }, // Support(s)
      9: { cellWidth: baseWidth * 0.5 }, // UI?
      10: { cellWidth: baseWidth * 1.5 } // Notes
    },
    margin: { left: 40, right: 40 },
    styles: { overflow: 'linebreak', cellPadding: 1.5 }
  });
  
  // Generate filename
  const filename = `HPC_Service_Report_${data.client.last_name}_${formatDate(data.startDate).replace(/\//g, '-')}_${formatDate(data.endDate).replace(/\//g, '-')}.pdf`;
  
  // Save PDF
  doc.save(filename);
};

export const generateTransportationReportPDF = (data: TransportationReportData): void => {
  const doc = new jsPDF('landscape', 'pt', 'a4');
  
  // Title
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('HPC TRANSPORTATION REPORT', doc.internal.pageSize.getWidth() / 2, 40, { align: 'center' });
  
  let yPos = 80;
  
  // Header Information
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  
  const leftCol = 50;
  const rightCol = 350;
  const lineHeight = 15;
  
  doc.setFont('helvetica', 'bold');
  doc.text('Individual\'s Name:', leftCol, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(`${data.client.first_name} ${data.client.last_name}`, leftCol + 100, yPos);
  
  doc.setFont('helvetica', 'bold');
  doc.text('Individual\'s Medicaid Number:', rightCol, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(data.client.medicaid_num ? String(data.client.medicaid_num) : 'N/A', rightCol + 150, yPos);
  
  yPos += lineHeight;
  
  const ispSpan = data.selectedISP && data.selectedISP.span_start_date && data.selectedISP.span_end_date
    ? `${formatDate(data.selectedISP.span_start_date)} - ${formatDate(data.selectedISP.span_end_date)}`
    : '_____________________';
  
  doc.setFont('helvetica', 'bold');
  doc.text('ISP Span:', leftCol, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(ispSpan, leftCol + 60, yPos);
  
  const dateRange = data.startDate && data.endDate
    ? `${formatDate(data.startDate)} - ${formatDate(data.endDate)}`
    : '';
  
  doc.setFont('helvetica', 'bold');
  doc.text('Date Range:', rightCol, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(dateRange, rightCol + 80, yPos);
  
  yPos += lineHeight;
  
  doc.setFont('helvetica', 'bold');
  doc.text('Provider\'s Name:', leftCol, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(`${data.provider.first_name} ${data.provider.last_name}`, leftCol + 100, yPos);
  
  doc.setFont('helvetica', 'bold');
  doc.text('Provider\'s Contract Number:', rightCol, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(data.provider.contract_number || 'N/A', rightCol + 150, yPos);
  
  yPos += lineHeight;
  
  doc.setFont('helvetica', 'bold');
  doc.text('County:', leftCol, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(getCountyName(data.client.county_id, data.ohioCounties), leftCol + 50, yPos);
  
  doc.setFont('helvetica', 'bold');
  doc.text('Provider\'s Electronic Signature:', rightCol, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text('_____________________', rightCol + 150, yPos);
  
  yPos += 30;
  
  // Prepare table data
  const tableData = data.transportations.map(transportation => [
    formatDate(transportation.transportation_date || transportation.scheduled_start),
    formatTime(transportation.scheduled_start),
    formatTime(transportation.scheduled_end),
    getVisitRatio(transportation.visit_id, data.visits),
    formatAddress(transportation.start_location_id, data.locations),
    formatAddress(transportation.end_location_id, data.locations),
    calculateMiles(transportation),
    transportation.notes || ''
  ]);
  
  // Generate table
  // A4 landscape: 842pt width, with 40pt margins = 762pt available
  // Distribute width across 8 columns proportionally
  const availableWidth = 762;
  // Proportional widths: Date(0.9) + TimeIn(0.9) + TimeOut(0.9) + Ratio(0.7) + StartAddr(1.6) + DestAddr(1.6) + Miles(0.7) + Notes(1.2) = 8.5
  const totalProportion = 8.5;
  const baseWidth = availableWidth / totalProportion;
  
  autoTable(doc, {
    startY: yPos,
    head: [['Date', 'Time In', 'Time Out', 'Ratio', 'Starting Address', 'Destination Address(es)', 'Miles', 'Notes']],
    body: tableData,
    theme: 'grid',
    headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7 },
    bodyStyles: { fontSize: 6 },
    columnStyles: {
      0: { cellWidth: baseWidth * 0.9 }, // Date
      1: { cellWidth: baseWidth * 0.9 }, // Time In
      2: { cellWidth: baseWidth * 0.9 }, // Time Out
      3: { cellWidth: baseWidth * 0.7 }, // Ratio
      4: { cellWidth: baseWidth * 1.6 }, // Starting Address
      5: { cellWidth: baseWidth * 1.6 }, // Destination Address(es)
      6: { cellWidth: baseWidth * 0.7 }, // Miles
      7: { cellWidth: baseWidth * 1.2 }  // Notes
    },
    margin: { left: 40, right: 40 },
    styles: { overflow: 'linebreak', cellPadding: 1.5 }
  });
  
  // Generate filename
  const filename = `HPC_Transportation_Report_${data.client.last_name}_${formatDate(data.startDate).replace(/\//g, '-')}_${formatDate(data.endDate).replace(/\//g, '-')}.pdf`;
  
  // Save PDF
  doc.save(filename);
};

